import { PageLoadingSpinner } from '@/components/PageLoadingSpinner/PageLoadingSpinner.tsx'
import { TenantProvider, useAuth, type AuthenticatedAuthState } from '@/contexts'
import { type Feature, FEATURES, isFeatureEnabled } from '@/utils/feature-flag.ts'
import { queryClient } from '@/utils/query-client.ts'
import {
  createFileRoute,
  Outlet,
  redirect,
  retainSearchParams,
  useRouter
} from '@tanstack/react-router'
import { memo, useEffect } from 'react'
import MiniDrawer from './-components/Drawer/Drawer.tsx'
import type { NavigationSection } from './-components/Drawer/types.ts'

import AssuredWorkloadOutlinedIcon from '@mui/icons-material/AssuredWorkloadOutlined'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DashboardIcon from '@mui/icons-material/Dashboard'
import EvStationIcon from '@mui/icons-material/EvStation'
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import RouteIcon from '@mui/icons-material/Route'
import SettingsIcon from '@mui/icons-material/Settings'
import StorefrontIcon from '@mui/icons-material/Storefront'
import { FleetAllocationIcon } from '@/components/Icons/FleetAllocationIcon.tsx'
import { CoercedStringArray } from '@/utils/common.schema.ts'
import { z } from 'zod'

// Temporary navigation structure to maintain compatibility
const navigationSections: NavigationSection[] = [
  {
    id: 'top',
    style: {
      flexGrow: 1,
      justifyContent: 'center'
    },
    items: [
      {
        type: 'navigation',
        id: 'dashboard',
        label: 'dashboard',
        to: '/$companySlug/dashboard',
        icon: <DashboardIcon color="primary" />,
        permissions: ['SITE_READ'],
        feature: FEATURES.DASHBOARD
      },
      {
        type: 'navigation',
        id: 'fleets',
        label: 'fleet',
        to: '/$companySlug/vehicles',
        icon: <LocalShippingIcon color="primary" />,
        permissions: ['SITE_READ'],
        feature: FEATURES.FLEET
      },
      {
        type: 'navigation',
        id: 'fleet-tracking',
        label: 'fleetTracking',
        to: '/$companySlug/fleet-tracking',
        icon: <MapOutlinedIcon color="primary" />,
        permissions: ['VEHICLE_READ', 'USER_READ'],
        feature: FEATURES.FLEET_ALLOCATION
      },
      {
        type: 'navigation',
        id: 'fleet-allocation',
        label: 'fleetAllocation',
        to: '/$companySlug/fleet-allocation',
        icon: <FleetAllocationIcon color="primary" />,
        permissions: ['VEHICLE_READ'],
        feature: FEATURES.FLEET_TRACKING
      },
      {
        type: 'navigation',
        id: 'charging-hubs',
        label: 'charging',
        to: '/$companySlug/charging-infrastructure',
        icon: <EvStationIcon color="primary" />,
        permissions: ['CHARGER_READ'],
        feature: FEATURES.CHARGING
      },
      {
        type: 'navigation',
        id: 'routes',
        label: 'route',
        to: '/$companySlug/route-planning',
        icon: <RouteIcon color="primary" />,
        permissions: ['ROUTING_READ'],
        feature: FEATURES.ROUTES
      },
      {
        type: 'navigation',
        id: 'reports',
        label: 'reports',
        to: '/$companySlug/reports',
        icon: <InsertChartOutlinedIcon color="primary" />,
        permissions: ['REPORTING_READ'],
        feature: FEATURES.REPORTS
      },
      {
        type: 'navigation',
        id: 'marketplace',
        label: 'marketplace',
        to: '/$companySlug/marketplace',
        icon: <StorefrontIcon color="primary" />,
        permissions: [],
        feature: FEATURES.MARKETPLACE
      },
      {
        type: 'navigation',
        id: 'users',
        label: 'userManagement',
        to: '/$companySlug/users',
        icon: <PersonOutlineIcon color="primary" />,
        permissions: ['USER_READ'],
        feature: FEATURES.USER_MANAGEMENT
      },
      {
        type: 'navigation',
        id: 'administration',
        label: 'administration',
        to: '/$companySlug/administration',
        icon: <AssuredWorkloadOutlinedIcon color="primary" />,
        permissions: [],
        feature: FEATURES.ADMINISTRATION
      }
    ]
  },
  {
    id: 'bottom',
    items: [
      {
        type: 'divider'
      },
      {
        type: 'navigation',
        id: 'user-settings',
        label: 'userSettings',
        to: '/$companySlug/user-settings',
        icon: <SettingsIcon color="primary" />,
        permissions: []
      },
      {
        type: 'toggle',
        id: 'collapse',
        label: 'collapse',
        icon: open =>
          open ? (
            <ChevronLeftIcon color="primary" />
          ) : (
            <ChevronRightIcon color="primary" />
          )
      }
    ]
  }
]

function getEnabledNavigationSections(): NavigationSection[] {
  return navigationSections
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        // If item has a feature flag, check if it's enabled
        if ('feature' in item && item.feature) {
          if (!isFeatureEnabled(item.feature as Feature)) return false
        }
        return true
      })
    }))
    .filter(section => section.items.length > 0) // Remove empty sections
}

const SearchSchema = z.object({
  sites: CoercedStringArray.optional()
})

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ context, location }) => {
    if (context.auth.status === 'PENDING') {
      // loader() returns the AuthenticatedAuthState once user data is fetched
      return { auth: await context.auth.loader() }
    }

    if (context.auth.status === 'ANONYMOUS') {
      throw redirect({
        to: '/login',
        search: {
          // Save current location for redirect after login
          redirect: location.href
        }
      })
    }

    // Already authenticated - narrow the type for child routes
    return {
      auth: context.auth
    }
  },
  pendingMs: 0,
  pendingComponent: Loader,
  component: Authenticated,
  validateSearch: SearchSchema,
  search: {
    middlewares: [retainSearchParams(['sites'])]
  }
})

function Loader() {
  // const { t } = useTranslation('global')
  // TODO: Translate when alex is done

  return <PageLoadingSpinner key="page-loader" title="Logging in..." />
}

/**
 * Components that redirect back to the login screen as soon as the user logs out, or is for some
 * reason unauthenticated.
 */
function AuthWatcher() {
  const router = useRouter()
  const { status } = useAuth()

  useEffect(() => {
    if (status === 'ANONYMOUS') {
      router.navigate({ to: '/login' }).then(() => {
        queryClient.clear()
      })
    }
  }, [status, router])

  return null
}

// Feature flags are static — compute once at module level
const enabledSections = getEnabledNavigationSections()

// Memoized so MatchInner re-rendering Authenticated doesn't cascade into the drawer/nav.
// Outlet re-renders itself independently via its own router subscription.
const Shell = memo(function Shell({ auth }: { auth: AuthenticatedAuthState }) {
  return (
    <MiniDrawer sections={enabledSections} auth={auth}>
      <Outlet />
    </MiniDrawer>
  )
})

function Authenticated() {
  const { auth } = Route.useRouteContext()

  return (
    <TenantProvider tenantId={null}>
      <AuthWatcher />
      <Shell auth={auth} />
    </TenantProvider>
  )
}
